import axios from '../axios';
import {
  AnalyticsOverviewResponse,
  AnalyticsReportResponse,
  AnalyticsSection,
  AdminAnalyticsFilters,
} from '../types/adminAnalytics.ts';

const getAuthHeaders = () => {
  const authToken = localStorage.getItem('access-Token-Edify');
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export function buildAnalyticsQuery(
  filters: AdminAnalyticsFilters,
  options?: { sections?: AnalyticsSection[]; format?: 'xlsx' | 'csv' }
): URLSearchParams {
  const q = new URLSearchParams();
  q.set('from', filters.from);
  q.set('to', filters.to);
  if (filters.useExplicitCompare && filters.compareFrom && filters.compareTo) {
    q.set('compareFrom', filters.compareFrom);
    q.set('compareTo', filters.compareTo);
  }
  if (filters.includeDeleted) q.set('includeDeleted', 'true');
  if (filters.usersValidatedOnly) q.set('usersValidatedOnly', 'true');
  const sections = options?.sections;
  if (sections?.length) {
    q.set('sections', sections.join(','));
  }
  if (options?.format) q.set('format', options.format);
  return q;
}

export const adminAnalyticsApi = {
  fetchOverview: async (filters: AdminAnalyticsFilters): Promise<AnalyticsOverviewResponse> => {
    const query = buildAnalyticsQuery(filters);
    const response = await axios.get<AnalyticsOverviewResponse>(
      `/admin/analytics/overview?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  fetchSection: async (
    section: AnalyticsSection,
    filters: AdminAnalyticsFilters
  ): Promise<AnalyticsOverviewResponse> => {
    const query = buildAnalyticsQuery(filters, { sections: [section] });
    const response = await axios.get<AnalyticsOverviewResponse>(
      `/admin/analytics/${section}?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  fetchReport: async (filters: AdminAnalyticsFilters): Promise<AnalyticsReportResponse> => {
    const query = buildAnalyticsQuery(filters);
    const response = await axios.get<AnalyticsReportResponse>(
      `/admin/analytics/report?${query}`,
      getAuthHeaders()
    );
    return response.data;
  },

  downloadExport: async (
    filters: AdminAnalyticsFilters,
    format: 'xlsx' | 'csv' = 'xlsx'
  ): Promise<{ blob: Blob; filename: string }> => {
    const query = buildAnalyticsQuery(filters, { format });
    const response = await axios.get(`/admin/analytics/export?${query}`, {
      ...getAuthHeaders(),
      responseType: 'blob',
    });
    const disposition = response.headers['content-disposition'] ?? '';
    const match = disposition.match(/filename="([^"]+)"/);
    const filename = match?.[1] ?? `edify-analytics.${format}`;
    return { blob: response.data, filename };
  },
};

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
